import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Flag, Icon, Chip, Button, Field } from './primitives.jsx';
import { formatCountdown, WC26_KICKOFF_UTC } from './countdown.js';
import { buildShareTargets } from './share.js';

// Trigger the native OS share sheet (mobile) or copy to clipboard (desktop fallback).
// Used by the QR-tap-to-share affordance on pool pages.
async function shareOrCopy(url, title = 'SWEEP:WC26') {
  if (navigator.share) {
    try { await navigator.share({ title, url }); return 'shared'; }
    catch (e) { /* user cancelled — silent */ return 'cancelled'; }
  }
  try { await navigator.clipboard.writeText(url); return 'copied'; }
  catch (e) { return 'failed'; }
}

// ============================================================================
// MyPools: lists pools the user has touched on this device (from localStorage)
// ============================================================================

const readKnownPools = () => {
  const known = new Map();
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    let id = null;
    let role = null;
    if (key.startsWith('sweep-wc26-secret-')) {
      id = key.slice('sweep-wc26-secret-'.length);
      role = 'admin';
    } else if (key.startsWith('sweep-wc26-drawn-')) {
      id = key.slice('sweep-wc26-drawn-'.length);
      role = 'participant';
    }
    if (id) {
      const existing = known.get(id);
      // admin role wins if user is both creator and participant
      if (!existing || (existing.role === 'participant' && role === 'admin')) {
        known.set(id, { id, role });
      }
    }
  }
  return Array.from(known.values());
};

export const MyPools = ({ onOpenPool, onOpenRecover }) => {
  const [pools, setPools] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const known = readKnownPools();
    if (known.length === 0) {
      setLoading(false);
      return;
    }

    fetch('/api/pools/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids: known.map(k => k.id) }),
    })
      .then(r => r.ok ? r.json() : [])
      .then(metadata => {
        const byId = new Map(metadata.map(p => [p.id, p]));
        // Drop stale entries whose pool has been deleted server-side, but keep
        // the localStorage key for the user to clean up themselves later.
        const merged = known
          .map(k => byId.has(k.id) ? { ...byId.get(k.id), role: k.role } : null)
          .filter(Boolean)
          .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setPools(merged);
      })
      .catch(() => setPools([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || pools.length === 0) {
    // Even with no cached pools, show the recover-by-email entry so users
    // returning on a new device can find their pools.
    return (
      <div style={{
        position: 'relative', zIndex: 2, textAlign: 'center',
        marginTop: 40, padding: '0 24px',
      }}>
        <button
          onClick={onOpenRecover}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 13,
            color: 'var(--ink-soft)', textDecoration: 'underline',
            letterSpacing: '0.04em',
          }}
        >
          Lost your link? Recover pools by email →
        </button>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative', zIndex: 2,
      maxWidth: 880, margin: '64px auto 0', padding: '0 24px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 12,
      }}>
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 14,
          letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--blue)',
        }}>YOUR POOLS</span>
        <button
          onClick={onOpenRecover}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: 'var(--font-mono)', fontSize: 12,
            color: 'var(--ink-soft)', textDecoration: 'underline',
            letterSpacing: '0.04em',
          }}
        >
          Lost a link? Recover by email →
        </button>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: 16,
      }}>
        {pools.map(p => (
          <button
            key={p.id}
            onClick={() => onOpenPool(p.id)}
            style={{
              textAlign: 'left', cursor: 'pointer',
              background: 'var(--paper-warm)',
              border: '1.5px solid var(--hairline-strong)',
              borderRadius: 10, padding: 20,
              display: 'flex', flexDirection: 'column', gap: 10,
              transition: 'border-color 120ms, transform 120ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--blue)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--hairline-strong)'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-soft)' }}>
                {p.id}
              </span>
              <Chip tone={p.role === 'admin' ? 'blue' : 'pale'}>
                {p.role === 'admin' ? 'ADMIN' : 'PARTICIPANT'}
              </Chip>
            </div>
            <div style={{
              fontFamily: 'var(--font-display)', fontSize: 20,
              letterSpacing: '-0.005em', lineHeight: 1.1,
              color: 'var(--ink)',
            }}>
              {p.name}
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-mute)',
              marginTop: 4, letterSpacing: '0.04em',
            }}>
              <span>STAGE: {p.stage.toUpperCase()}</span>
              <span>OPEN →</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Screen: Hero / Homepage
// ============================================================================

const KickoffCountdown = () => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const { phase, days, hours, minutes, seconds } = formatCountdown(WC26_KICKOFF_UTC, now);
  if (phase === 'post') return null;

  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div style={{
      background: 'var(--ink)', color: 'var(--paper)',
      padding: '14px 24px', textAlign: 'center',
      fontFamily: 'var(--font-display)', letterSpacing: '0.12em',
      textTransform: 'uppercase', fontSize: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      gap: 16, flexWrap: 'wrap',
      position: 'relative', zIndex: 3,
    }}>
      {phase === 'pre' ? (
        <>
          <span style={{ color: 'rgba(255,255,255,0.7)' }}>Kickoff in</span>
          <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: 18, letterSpacing: '0.04em' }}>
            <span aria-live="polite">{days}</span>
            <span style={{ color: 'rgba(255,255,255,0.5)', margin: '0 6px' }}>d</span>
            {pad(hours)}<span style={{ color: 'rgba(255,255,255,0.4)' }}>:</span>
            {pad(minutes)}<span style={{ color: 'rgba(255,255,255,0.4)' }}>:</span>
            {pad(seconds)}
          </span>
        </>
      ) : (
        <span style={{ color: 'var(--gold)' }}>Tournament live — follow on your dashboard</span>
      )}
    </div>
  );
};

export const Hero = ({ onCreate, onRules, onAbout, onOpenPool, onOpenRecover }) => {
  return (
    <section style={{
      position: 'relative', minHeight: 'var(--h-full)',
      background: 'var(--paper)', overflow: 'hidden',
      // The fixed Header sits at top with z-index 50. We need content to
      // start below it. The countdown renders first (just below the header),
      // then the balls + title get the original ~120px breathing room.
      paddingTop: 60, paddingBottom: 0,
    }}>
      <KickoffCountdown />
      <div style={{ height: 60 }} />
      <style>{`
        @keyframes ball-spin-l { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(360deg); } }
        @keyframes ball-spin-r { from { transform: translateY(-50%) rotate(0deg); } to { transform: translateY(-50%) rotate(-360deg); } }
      `}</style>

      {/* Balls live in a height-clipped container so they frame the title/CTAs
          but don't bleed down into the YOUR POOLS section below the buttons. */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: '78vh', overflow: 'hidden', pointerEvents: 'none', zIndex: 0,
      }}>
        <img src="/assets/ball.svg" alt="" style={{
          position: 'absolute', left: '-18vw', top: '48%',
          width: '48vw', maxWidth: 820,
          animation: 'ball-spin-l 90s linear infinite',
          transformOrigin: 'center',
          opacity: 0.85
        }} />

        <img src="/assets/ball.svg" alt="" style={{
          position: 'absolute', right: '-18vw', top: '48%',
          width: '48vw', maxWidth: 820,
          animation: 'ball-spin-r 110s linear infinite',
          transformOrigin: 'center',
          opacity: 0.85
        }} />
      </div>

      <div style={{
        position: 'relative', textAlign: 'center', marginBottom: 32, zIndex: 2,
      }}>
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 14,
          letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--blue)',
        }}>OFFICE SWEEPSTAKE · FIFA WORLD CUP 26™</span>
      </div>

      <div style={{
        position: 'relative', zIndex: 2, padding: '0 24px',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
      }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          // Drop floor from 72→56 so iPhone SE / Mini don't horizontal-scroll.
          // No nowrap: on screens <360px the title naturally wraps to
          // "SWEEP:" / "WC26" which reads as deliberate, not broken.
          fontSize: 'clamp(56px, 14vw, 180px)',
          lineHeight: 0.85, letterSpacing: '-0.015em',
          textTransform: 'uppercase', margin: 0, textAlign: 'center',
          color: 'var(--ink)',
        }}>
          SWEEP<span style={{ color: 'var(--blue)' }}>:</span><span style={{ color: 'var(--gold)' }}>WC26</span>
        </h1>
      </div>

      <div style={{ position: 'relative', zIndex: 2, textAlign: 'center', marginTop: 24 }}>
        <img src="/assets/trophy.svg" alt="" style={{ width: 88, height: 'auto' }} />
      </div>

      <div style={{
        position: 'relative', zIndex: 2, textAlign: 'center',
        marginTop: 32, padding: '0 24px',
      }}>
        <p style={{
          maxWidth: 640, margin: '0 auto', fontSize: 20, lineHeight: 1.5,
          color: 'var(--ink)',
        }}>
          The office sweepstake, built for <em style={{ fontStyle: 'italic', color: 'var(--blue)' }}>World Cup 2026.</em>
          <br/>
          48 teams · 12 groups · 104 matches · 1 winner.
        </p>

        <div style={{
          marginTop: 40, display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap',
        }}>
          <Button variant="large" onClick={onCreate} style={{ whiteSpace: 'nowrap' }}>
            CREATE A POOL <Icon name="arrow-right" size={20} />
          </Button>
          <Button variant="secondary" onClick={onRules} style={{ whiteSpace: 'nowrap' }}>HOW IT WORKS</Button>
          <Button variant="secondary" onClick={onAbout} style={{ whiteSpace: 'nowrap' }}>ABOUT</Button>
        </div>
      </div>

      <MyPools onOpenPool={onOpenPool} onOpenRecover={onOpenRecover} />

      <div style={{
        marginTop: 96, background: 'var(--ink)', color: 'var(--paper)',
        padding: '40px 32px', position: 'relative', zIndex: 2
      }}>
        <div style={{
          maxWidth: 1280, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 32,
        }}>
          {[
            ['48',  'TEAMS'],
            ['12',  'GROUPS'],
            ['104', 'MATCHES'],
            ['1',   'WALKOUT PER PICK'],
          ].map(([n, l]) => (
            <div key={l} style={{ borderLeft: '2px solid rgba(255,255,255,0.15)', paddingLeft: 24 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 72, letterSpacing: '-0.01em', lineHeight: 0.9 }}>{n}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', marginTop: 12 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Screen: SetupForm
// ============================================================================

export const SetupForm = ({ onCreated, onBack }) => {
  const [name, setName] = React.useState('Office Sweepstake 2026');
  const [email, setEmail] = React.useState('');
  const [numPlayers, setNumPlayers] = React.useState(10);
  const [rounds, setRounds] = React.useState(1);
  const [drawMode, setDrawMode] = React.useState('standard');
  const [nameErr, setNameErr] = React.useState('');
  const [emailErr, setEmailErr] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  // Two modes: 'office' (default, open) and 'business' (gated by a code).
  const [mode, setMode] = React.useState('office');
  const [bizCode, setBizCode] = React.useState('');
  const [bizLabel, setBizLabel] = React.useState(''); // set on successful verify
  const [bizErr, setBizErr] = React.useState('');
  const [verifying, setVerifying] = React.useState(false);
  const [slug, setSlug] = React.useState('');
  const [slugErr, setSlugErr] = React.useState('');
  const [brandName, setBrandName] = React.useState('');
  const [logoUrl, setLogoUrl] = React.useState('');
  const [accent, setAccent] = React.useState('#1d4ed8');
  const [prizeBlurb, setPrizeBlurb] = React.useState('');
  const [games, setGames] = React.useState({ eliminator: true, predictor: false, sweep: false });

  // Business mode is only "armed" once the authorization code verifies.
  const whiteLabel = mode === 'business' && !!bizLabel;

  const verifyCode = async () => {
    if (!bizCode.trim()) { setBizErr('Enter your authorization code.'); return; }
    setVerifying(true); setBizErr('');
    try {
      const res = await fetch('/api/business/verify', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: bizCode.trim() }),
      });
      const body = await res.json();
      if (res.ok && body.ok) { setBizLabel(body.label); }
      else { setBizLabel(''); setBizErr('That code isn’t valid.'); }
    } catch { setBizErr('Network error — try again.'); }
    finally { setVerifying(false); }
  };

  // Rounds × players must fit inside the 48-team pool. If the user lowers the
  // player count and that pushes rounds above its new ceiling, clamp it down
  // so the form can never submit an invalid pair.
  const maxRounds = Math.floor(48 / numPlayers);
  React.useEffect(() => {
    if (rounds > maxRounds) setRounds(maxRounds);
  }, [numPlayers, maxRounds, rounds]);

  const chooseDrawMode = (nextMode) => {
    setDrawMode(nextMode);
    if (nextMode === 'tiered-balanced') {
      setNumPlayers(24);
      setRounds(2);
    }
  };

  const totalTeamsUsed = numPlayers * rounds;

  const submit = async (e) => {
    e.preventDefault();
    let ok = true;
    if (!name || name.length > 100) { setNameErr('Pool name must be 1–100 characters'); ok = false; } else setNameErr('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setEmailErr('Enter a valid email — e.g. luke@example.com'); ok = false; } else setEmailErr('');

    if (ok) {
      setLoading(true);
      try {
        const res = await fetch('/api/pool/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(
            whiteLabel
              ? { poolName: name, adminEmail: email, identityMode: 'handle',
                  businessCode: bizCode.trim(),
                  ...(slug.trim() && { slug: slug.trim() }),
                  enabledGames: Object.keys(games).filter((g) => games[g]),
                  branding: { name: brandName || name, logoUrl, accent, prizeBlurb } }
              : { poolName: name, adminEmail: email, numPlayers, rounds, drawMode }
          )
        });
        const data = await res.json();
        if (res.ok) {
          onCreated({ id: data.poolId, name, email, secret: data.adminSecret, slug: data.slug });
        } else if (data.code && data.code.startsWith('slug_')) {
          setSlugErr(data.error);
        } else {
          setEmailErr(data.error || 'Failed to create pool');
        }
      } catch (err) {
        setEmailErr('Network error creating pool');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <section style={{
      minHeight: 'var(--h-full)', display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
      paddingTop: 80,
    }}>
      <div style={{
        background: 'var(--ink)', color: 'var(--paper)',
        padding: 'clamp(40px, 8vw, 80px) clamp(24px, 6vw, 56px)',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'space-between', minHeight: 'calc(var(--h-full) - 80px)',
        position: 'relative', overflow: 'hidden',
      }}>
        {/* Decorative ball removed in V2 — the old hue-rotate filter was tuned
            for the single-colour blue ball and produced a muddy purple smear
            against the multi-coloured Trionda redesign. The dark panel reads
            cleanly without it. */}
        <div style={{ position: 'relative' }}>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 14,
            letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)',
          }}>STEP 1 OF 1 · POOL SETUP</span>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(56px, 6vw, 96px)',
            letterSpacing: '-0.005em', textTransform: 'uppercase', lineHeight: 0.9,
            margin: '24px 0 0',
          }}>
            START THE<br/>SWEEP.
          </h1>
          <p style={{
            marginTop: 32, fontSize: 16, lineHeight: 1.55,
            color: 'rgba(255,255,255,0.78)', maxWidth: 380,
          }}>
            You'll get a shareable URL, a QR code, and an admin secret to manage re-rolls and bracket advancement.
          </p>
        </div>
        <div style={{
          position: 'relative',
          fontFamily: 'var(--font-mono)', fontSize: 12,
          color: 'rgba(255,255,255,0.45)', letterSpacing: '0.08em',
        }}>multi-round draft · no login required</div>
      </div>

      <form onSubmit={submit} style={{
        padding: 'clamp(40px, 8vw, 80px) clamp(24px, 6vw, 56px)',
        display: 'flex', flexDirection: 'column',
        justifyContent: 'center', gap: 32, maxWidth: 560,
      }}>
        <h2 style={{
          fontFamily: 'var(--font-display)', fontSize: 36,
          textTransform: 'uppercase', letterSpacing: '-0.005em',
          margin: 0,
        }}>YOUR DETAILS</h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['office', 'Office Sweepstake'], ['business', 'Professional / Business']].map(([key, lbl]) => (
              <button key={key} type="button" onClick={() => setMode(key)}
                style={{
                  flex: 1, padding: '10px 12px', cursor: 'pointer',
                  fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  background: mode === key ? 'var(--ink)' : 'var(--paper-warm)',
                  color: mode === key ? 'var(--paper)' : 'var(--ink-soft)',
                  border: '1.5px solid var(--hairline-strong)', borderRadius: 8,
                }}>
                {lbl}
              </button>
            ))}
          </div>
          <Field label="Pool name" value={name} onChange={setName}
                 placeholder="Office Sweepstake 2026"
                 hint="3–100 characters · shown to participants"
                 error={nameErr} />
          <Field label="Admin email" value={email} onChange={setEmail}
                 type="email" placeholder="you@company.com"
                 hint="Used to recover the admin secret if you lose it"
                 error={emailErr} />

          {mode === 'business' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {!bizLabel ? (
                <>
                  <Field label="Authorization code" value={bizCode} onChange={(v) => { setBizCode(v); setBizErr(''); }}
                         placeholder="enter your code" hint="Business mode is invite-only." error={bizErr} />
                  <Button variant="secondary" type="button" onClick={verifyCode} disabled={verifying}>
                    {verifying ? 'CHECKING…' : 'UNLOCK BUSINESS MODE'}
                  </Button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--success)', fontFamily: 'var(--font-mono)' }}>
                    ✓ {bizLabel}
                  </div>
                  <Field label="Brand name" value={brandName} onChange={setBrandName} placeholder="e.g. Keiren Tips" hint="Shown to entrants in place of the pool name." />
                  <Field label="Custom address (slug)" value={slug} onChange={(v) => { setSlug(v); setSlugErr(''); }}
                         placeholder="e.g. TTS" hint="Your login URL: sweepwc26.com/<slug> — letters, numbers, hyphens." error={slugErr} />
                  <Field label="Logo URL" value={logoUrl} onChange={setLogoUrl} placeholder="https://…/logo.png" hint="Pasted image URL (optional)." />
                  <Field label="Accent colour" value={accent} onChange={setAccent} placeholder="#1d4ed8" hint="Hex colour for buttons/highlights." />
                  <Field label="Prize blurb" value={prizeBlurb} onChange={setPrizeBlurb} placeholder="e.g. £100 to the last 3 standing" hint="Optional reward note shown to entrants." />
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {['eliminator', 'predictor', 'sweep'].map((g) => (
                      <label key={g} style={{ display: 'flex', alignItems: 'center', gap: 8, textTransform: 'capitalize' }}>
                        <input type="checkbox" checked={!!games[g]} onChange={(e) => setGames((s) => ({ ...s, [g]: e.target.checked }))} />
                        {g}
                      </label>
                    ))}
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>Slice 1 ships Eliminator; Predictor &amp; Sweep light up in later slices.</span>
                </>
              )}
            </div>
          )}

          {mode === 'office' && (
          <div style={{
            background: 'var(--paper-warm)',
            border: '1.5px solid var(--hairline-strong)',
            borderRadius: 10, padding: 20,
            display: 'flex', flexDirection: 'column', gap: 16,
          }}>
            <span style={{
              fontFamily: 'var(--font-display)', fontSize: 13,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'var(--blue)',
            }}>POOL SETTINGS</span>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              {[
                ['standard', 'Classic random', 'Any team can appear in any round.'],
                ['tiered-balanced', 'Balanced tiered', '24 players · stronger teams first, underdogs second.'],
              ].map(([key, title, desc]) => {
                const active = drawMode === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => chooseDrawMode(key)}
                    aria-pressed={active}
                    style={{
                      textAlign: 'left',
                      background: active ? 'var(--ink)' : 'var(--paper)',
                      color: active ? 'var(--paper)' : 'var(--ink)',
                      border: `1.5px solid ${active ? 'var(--ink)' : 'var(--hairline-strong)'}`,
                      borderRadius: 8,
                      padding: 14,
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{
                      display: 'block',
                      fontFamily: 'var(--font-display)',
                      fontSize: 13,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}>{title}</span>
                    <span style={{
                      display: 'block',
                      marginTop: 6,
                      fontSize: 12,
                      lineHeight: 1.35,
                      color: active ? 'rgba(255,255,255,0.72)' : 'var(--ink-soft)',
                    }}>{desc}</span>
                  </button>
                );
              })}
            </div>

            {/* auto-fit lets the two selects stack on phones <340px where
                a 2-up split would crush them. Above ~340px they sit side-by-side. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--ink)',
                }}>Number of players</span>
                <select
                  value={numPlayers}
                  onChange={(e) => {
                    setNumPlayers(Number(e.target.value));
                    if (drawMode === 'tiered-balanced') setDrawMode('standard');
                  }}
                  style={{
                    background: 'var(--paper)',
                    border: '1.5px solid var(--hairline-strong)',
                    borderRadius: 8, padding: '14px 16px',
                    fontFamily: 'var(--font-body)', fontSize: 16,
                    color: 'var(--ink)', outline: 'none', cursor: 'pointer',
                  }}
                >
                  {Array.from({ length: 48 }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                  Max 48 — the size of the tournament.
                </span>
              </label>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <span style={{
                  fontFamily: 'var(--font-display)', fontSize: 13, letterSpacing: '0.1em',
                  textTransform: 'uppercase', color: 'var(--ink)',
                }}>Rounds</span>
                <select
                  value={rounds}
                  onChange={(e) => {
                    setRounds(Number(e.target.value));
                    if (drawMode === 'tiered-balanced') setDrawMode('standard');
                  }}
                  style={{
                    background: 'var(--paper)',
                    border: '1.5px solid var(--hairline-strong)',
                    borderRadius: 8, padding: '14px 16px',
                    fontFamily: 'var(--font-body)', fontSize: 16,
                    color: 'var(--ink)', outline: 'none', cursor: 'pointer',
                  }}
                >
                  {Array.from({ length: maxRounds }, (_, i) => i + 1).map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                  Teams each player draws (one per round).
                </span>
              </label>
            </div>

            <div style={{
              borderTop: '1px solid var(--hairline)', paddingTop: 12,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              fontFamily: 'var(--font-mono)', fontSize: 12,
              letterSpacing: '0.06em', color: 'var(--ink-soft)',
            }}>
              <span>{numPlayers} PLAYERS × {rounds} ROUND{rounds === 1 ? '' : 'S'}</span>
              <span style={{ color: 'var(--ink)' }}>
                {totalTeamsUsed}/48 TEAMS USED
              </span>
            </div>

            {drawMode === 'tiered-balanced' && (
              <span style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.45 }}>
                Strength-3 teams are shuffled into either round when the pool is created, so every tiered pool gets its own split.
              </span>
            )}
          </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'CREATING...' : 'CREATE POOL'} <Icon name="arrow-right" size={16} />
          </Button>
          <Button variant="secondary" onClick={onBack} disabled={loading}>
            BACK
          </Button>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--ink-mute)', letterSpacing: '0.08em' }}>
            ↩ ENTER TO SUBMIT
          </span>
        </div>
      </form>
    </section>
  );
};

// ============================================================================
// Screen: PoolJoin
// ============================================================================

export const PoolJoin = ({ pool, onPick, assignedCount, onBack }) => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    setError('');
    const result = await onPick(name.trim(), email.trim());
    if (result && !result.ok) setError(result.error);
  };

  const shareUrl = window.location.host + window.location.pathname;
  const fullShareUrl = window.location.href;

  return (
    <section style={{
      minHeight: 'var(--h-full)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '128px 24px 64px', textAlign: 'center', position: 'relative',
      background: 'var(--paper)',
    }}>
      <img src="/assets/ball.svg" alt="" style={{
        position: 'absolute', left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)', width: '70vw', maxWidth: 900,
        opacity: 0.06, pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 720, width: '100%', position: 'relative' }}>
        <span style={{
          fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
          textTransform: 'uppercase', color: 'var(--blue)',
        }}>POOL · {pool.id.toUpperCase()}</span>

        <h1 style={{
          fontFamily: 'var(--font-display)', fontSize: 'clamp(48px, 7vw, 96px)',
          letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.9,
          margin: '20px 0 8px', textWrap: 'balance',
        }}>
          {pool.name.toUpperCase()}
        </h1>
        <p style={{ color: 'var(--ink-soft)', fontSize: 18, maxWidth: 520, margin: '24px auto 0' }}>
          You're about to be assigned a random team. The reveal plays once — no re-picks.
        </p>

        <form onSubmit={onSubmit} style={{ maxWidth: 380, margin: '36px auto 0', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label="Your Name"
            value={name}
            onChange={(val) => { setName(val); setError(''); }}
            placeholder="e.g. Luke"
            hint="Enter your name to draw your team"
            error={error}
          />
          <Field
            label="Email (optional)"
            value={email}
            onChange={(val) => setEmail(val)}
            placeholder="e.g. luke@work.com"
            hint="We'll send your team so you can show it off"
            type="email"
          />
          <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', width: '100%' }}>
            <Button variant="large" type="submit" style={{ fontSize: 22, padding: '16px 36px', flex: 1, whiteSpace: 'nowrap' }}>
              PICK MY TEAM <Icon name="arrow-right" size={18} />
            </Button>
            {onBack && (
              <Button variant="secondary" onClick={onBack} style={{ padding: '16px 24px' }}>
                DASHBOARD
              </Button>
            )}
          </div>
        </form>

        <div style={{
          display: 'flex', gap: 10, justifyContent: 'center', marginTop: 40, marginBottom: 56,
          flexWrap: 'wrap',
        }}>
          <Chip tone="blue">{assignedCount}/48 ASSIGNED</Chip>
          <Chip tone="pale">12 GROUPS</Chip>
          <Chip>NO LOGIN</Chip>
        </div>

        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 20,
          padding: '16px 20px', borderRadius: 8,
          background: 'var(--paper)', border: '1.5px solid var(--hairline-strong)',
          flexWrap: 'wrap', justifyContent: 'center'
        }}>
          <button
            type="button"
            onClick={() => shareOrCopy(fullShareUrl, `Join ${pool.name}`).then(r => { if (r === 'copied') { setCopied(true); setTimeout(() => setCopied(false), 2000); } })}
            aria-label="Share pool — opens system share sheet on mobile, copies on desktop"
            style={{
              background: '#fff', padding: 8, borderRadius: 6,
              border: '1px solid var(--hairline)', lineHeight: 0, cursor: 'pointer',
            }}
          >
            <QRCodeSVG value={fullShareUrl} size={72} level="M" />
          </button>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
              SHARE THIS POOL
            </div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, color: 'var(--blue)', marginTop: 2 }}>
              {shareUrl}
            </div>
          </div>
          <Button variant="secondary" onClick={handleCopy}>
            <Icon name={copied ? "check" : "copy"} size={14} />{copied ? "COPIED" : "COPY"}
          </Button>
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Screen: PoolSuccess
// ============================================================================

export const PoolSuccess = ({ poolId, poolName, slug, adminSecret, adminEmail, onProceed, onBackHome }) => {
  const [copiedLink, setCopiedLink] = React.useState(false);
  const [copiedSecret, setCopiedSecret] = React.useState(false);

  const shareUrl = slug
    ? `${window.location.protocol}//${window.location.host}/${slug}`
    : `${window.location.protocol}//${window.location.host}/pool/${poolId}`;
  // poolName is optional for backward-compat — fall back to a generic message
  // so the share row still works even if a caller forgets to pass it.
  const shareTargets = buildShareTargets({ url: shareUrl, poolName: poolName || 'World Cup 2026' });

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const copySecret = () => {
    navigator.clipboard.writeText(adminSecret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  return (
    <section style={{
      minHeight: 'var(--h-full)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '128px 24px 64px', background: 'var(--paper)',
    }}>
      <div style={{ maxWidth: 640, width: '100%', display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div>
          <span style={{
            fontFamily: 'var(--font-display)', fontSize: 14, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'var(--success)',
          }}>SUCCESS · POOL CREATED</span>

          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(44px, 6vw, 64px)',
            letterSpacing: '-0.01em', textTransform: 'uppercase', lineHeight: 0.95,
            margin: '16px 0 8px', color: 'var(--ink)'
          }}>
            CHECK YOUR INBOX
          </h1>
          <p style={{ color: 'var(--ink-soft)', fontSize: 16, margin: '16px 0 0', lineHeight: 1.5 }}>
            A simulation email has been sent to <strong style={{ color: 'var(--ink)' }}>{adminEmail}</strong> containing your pool link and admin secret. You can also view the raw file at <code style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--blue)' }}>data/emails/{poolId}_welcome.txt</code>.
          </p>
        </div>

        <div style={{
          background: 'var(--paper-warm)',
          border: '1.5px solid var(--hairline-strong)',
          borderRadius: 12,
          padding: 28,
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          gap: 20
        }}>
          <div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.08em', color: 'var(--ink-soft)' }}>POOL SHAREABLE LINK</span>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => shareOrCopy(shareUrl, 'Join my SWEEP:WC26 pool').then(r => { if (r === 'copied') { setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); } })}
                aria-label="Share pool — opens system share sheet on mobile, copies on desktop"
                style={{
                  background: '#fff', padding: 8, borderRadius: 6,
                  border: '1px solid var(--hairline)', lineHeight: 0, flexShrink: 0, cursor: 'pointer',
                }}
              >
                <QRCodeSVG value={shareUrl} size={104} level="M" />
              </button>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 220 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--blue)', background: '#fff', padding: '10px 14px', borderRadius: 6, border: '1px solid var(--hairline)', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                  {shareUrl}
                </div>
                <Button variant="secondary" onClick={copyLink} style={{ padding: '10px 18px', fontSize: 14, alignSelf: 'flex-start' }}>
                  <Icon name={copiedLink ? "check" : "copy"} size={14} /> {copiedLink ? "COPIED LINK" : "COPY LINK"}
                </Button>
              </div>
            </div>
            {/* Share row: one tap to drop the pool link into WhatsApp / Telegram / X / Email.
                This is the highest-leverage distribution moment — admin has just created
                the pool, nobody else is in it, and they're about to message colleagues. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginTop: 12 }}>
              {[
                { label: 'WHATSAPP', color: '#25D366',    href: shareTargets.whatsapp },
                { label: 'TELEGRAM', color: '#229ED9',    href: shareTargets.telegram },
                { label: 'X',        color: '#000000',    href: shareTargets.twitter },
                { label: 'EMAIL',    color: 'var(--ink)', href: shareTargets.email },
              ].map(s => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    background: s.color, color: '#fff', textDecoration: 'none',
                    padding: '10px 6px', borderRadius: 6, textAlign: 'center',
                    fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.06em',
                    textTransform: 'uppercase', fontWeight: 700,
                  }}
                >
                  {s.label}
                </a>
              ))}
            </div>
            <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--ink-mute)', fontFamily: 'var(--font-mono)', letterSpacing: '0.04em' }}>
              SCAN OR SHARE THE LINK — COLLEAGUES JOIN AS PARTICIPANTS, NO LOGIN REQUIRED.
            </p>
          </div>

          <div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 12, letterSpacing: '0.08em', color: 'var(--ink-soft)' }}>ADMIN SECRET CODE</span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 6 }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--ink)', background: '#fff', padding: '10px 14px', borderRadius: 6, border: '1px solid var(--hairline)', flex: 1 }}>
                {adminSecret}
              </div>
              <Button variant="secondary" onClick={copySecret} style={{ padding: '10px 18px', fontSize: 14 }}>
                <Icon name={copiedSecret ? "check" : "copy"} size={14} /> {copiedSecret ? "COPIED" : "COPY"}
              </Button>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, color: 'var(--ink-soft)', fontSize: 13, lineHeight: 1.45 }}>
              <Icon name="check" size={13} />
              <span>
                Saved automatically on this device — you'll stay signed in as admin. We've also emailed it to <strong style={{ color: 'var(--ink)' }}>{adminEmail}</strong>. Lost both? Use <em>Lost your link?</em> on the homepage to recover.
              </span>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="large" onClick={onProceed} style={{ width: '100%', maxWidth: 280 }}>
            ENTER SWEEPSTAKE <Icon name="arrow-right" size={18} />
          </Button>
          <Button variant="secondary" onClick={onBackHome} style={{ width: '100%', maxWidth: 280 }}>
            BACK TO HOME
          </Button>
        </div>
      </div>
    </section>
  );
};
